/**
 * Returns true if the string is a valid latitude value (-90 to 90).
 */
export function isValidLatitude(lat: string): boolean {
  const regex = /^-?\d+(\.\d+)?$/;
  return regex.test(lat) && parseFloat(lat) >= -90 && parseFloat(lat) <= 90;
}

/**
 * Returns true if the string is a valid longitude value (-180 to 180).
 */
export function isValidLongitude(lon: string): boolean {
  const regex = /^-?\d+(\.\d+)?$/;
  return regex.test(lon) && parseFloat(lon) >= -180 && parseFloat(lon) <= 180;
}

/**
 * Returns true if the HTTPS URL matches the expected pattern.
 *
 * @param url - URL to validate
 * @param checkFileType - When true, requires a .png or .gif extension
 */
export const isValidUrl = (url: string, checkFileType: boolean): boolean => {
  const urlTestWithFileType =
    /^https:\/\/[a-zA-Z0-9.-]+\.[a-z]{2,}(\/.*)?\.(png|gif)$/i;
  const urlPattern = /^https:\/\/[a-zA-Z0-9.-]+\.[a-z]{2,}(\/.*)?$/i;
  return checkFileType ? urlTestWithFileType.test(url) : urlPattern.test(url);
};

/**
 * Returns true if text byte-length (ASCII=1, non-ASCII=2) is within the limit.
 *
 * Use this only for limits defined in bytes. For limits defined in characters
 * (e.g. Auth0 profile attributes) use `isWithinCharacterCountLimit` instead,
 * otherwise accented names such as "Peña" are penalised for every non-ASCII letter.
 */
export const isWithinCharacterLimit = (
  text: string,
  characterLimit: number
): boolean => {
  let textLength = 0;
  for (const char of text) {
    textLength += char.charCodeAt(0) <= 0x7f ? 1 : 2;
  }
  return textLength <= characterLimit;
};

/**
 * Returns the number of characters in the text, counting every character as 1
 * regardless of its byte width.
 *
 * The text is normalised to NFC first so a letter typed as a base character plus
 * a combining mark (e.g. "n" + "◌̃") counts the same as its precomposed form ("ñ").
 * Code points outside the BMP (emoji, rare CJK) also count as 1 rather than 2.
 */
export const countCharacters = (text: string): number =>
  Array.from(text.normalize("NFC")).length;

/**
 * Returns true if the character count (see `countCharacters`) is within the limit.
 */
export const isWithinCharacterCountLimit = (
  text: string,
  characterLimit: number
): boolean => countCharacters(text) <= characterLimit;
